import React, { useEffect, useRef, useState } from "react";
import "./Charts.css";

const PAD = { top: 14, right: 16, bottom: 30, left: 46 };
const FALLBACK = { width: 620, height: 280 };


/**
 * Marker shapes double as a non-colour cue for each series, which the palette's
 * colour-vision separation relies on.
 */
function Marker({ shape, cx, cy, r, fill }) {
  switch (shape) {
    case "square":
      return (
        <rect
          x={cx - r}
          y={cy - r}
          width={r * 2}
          height={r * 2}
          fill={fill}
          stroke="#fff"
          strokeWidth="1.6"
        />
      );
    case "triangle":
      return (
        <path
          d={`M${cx},${cy - r * 1.15} L${cx + r * 1.1},${cy + r * 0.85} L${
            cx - r * 1.1
          },${cy + r * 0.85} Z`}
          fill={fill}
          stroke="#fff"
          strokeWidth="1.6"
        />
      );
    case "diamond":
      return (
        <path
          d={`M${cx},${cy - r * 1.3} L${cx + r * 1.3},${cy} L${cx},${
            cy + r * 1.3
          } L${cx - r * 1.3},${cy} Z`}
          fill={fill}
          stroke="#fff"
          strokeWidth="1.6"
        />
      );
    case "cross":
      return (
        <path
          d={`M${cx - r},${cy} H${cx + r} M${cx},${cy - r} V${cy + r}`}
          stroke={fill}
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      );
    default:
      return (
        <circle cx={cx} cy={cy} r={r} fill={fill} stroke="#fff" strokeWidth="1.6" />
      );
  }
}

const SHAPES = ["circle", "diamond", "square", "triangle", "cross"];

/**
 * Multi-series line chart over time with a shared crosshair tooltip.
 * `series` is [{ key, label, color, values: number[] }] and `labels` the x axis.
 */
export default function MultiTrendChart({
  labels,
  series,
  max,
  formatValue = (value) => value.toLocaleString("en-US"),
  formatTick = (value) => `${Math.round(value / 1000)}K`,
  tickCount = 5,
}) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState(FALLBACK);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const element = wrapRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({
        width: Math.max(width, 280),
        height: Math.max(height, 200),
      });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const { width, height } = size;
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;

  const ceiling =
    max ??
    Math.max(...series.flatMap((item) => item.values)) * 1.1;

  const x = (index) =>
    PAD.left +
    (labels.length === 1 ? plotW / 2 : (index / (labels.length - 1)) * plotW);
  const y = (value) => PAD.top + plotH - (value / ceiling) * plotH;

  const ticks = Array.from(
    { length: tickCount + 1 },
    (_, index) => (ceiling / tickCount) * index
  );

  const handleMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const position = ((event.clientX - rect.left) / rect.width) * width;
    let nearest = 0;
    labels.forEach((_, index) => {
      if (Math.abs(x(index) - position) < Math.abs(x(nearest) - position)) {
        nearest = index;
      }
    });
    setHover(nearest);
  };

  const tooltipFlipped = hover !== null && x(hover) > PAD.left + plotW * 0.55;

  return (
    <div className="mtrend">
      <div className="mtrend__plot" ref={wrapRef}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="mtrend__svg"
          role="img"
          aria-label={`Issue counts by month for ${series
            .map((item) => item.label)
            .join(", ")}`}
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={PAD.left + plotW}
                y1={y(tick)}
                y2={y(tick)}
                className="trend__grid"
              />
              <text
                x={PAD.left - 10}
                y={y(tick)}
                className="trend__tick trend__tick--y"
              >
                {tick === 0 ? "0" : formatTick(tick)}
              </text>
            </g>
          ))}

          {labels.map((label, index) => (
            <text
              key={label}
              x={x(index)}
              y={height - 8}
              className="trend__tick"
            >
              {label}
            </text>
          ))}

          {hover !== null && (
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={PAD.top}
              y2={PAD.top + plotH}
              className="trend__crosshair"
            />
          )}

          {series.map((item) => (
            <path
              key={item.key}
              d={item.values
                .map(
                  (value, index) =>
                    `${index === 0 ? "M" : "L"}${x(index)},${y(value)}`
                )
                .join(" ")}
              fill="none"
              stroke={item.color}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}

          {series.map((item, seriesIndex) =>
            item.values.map((value, index) => (
              <Marker
                key={`${item.key}-${labels[index]}`}
                shape={SHAPES[seriesIndex % SHAPES.length]}
                cx={x(index)}
                cy={y(value)}
                r={hover === index ? 4.6 : 3.6}
                fill={item.color}
              />
            ))
          )}
        </svg>

        {hover !== null && (
          <div
            className={`trend__tooltip${
              tooltipFlipped ? " trend__tooltip--flipped" : ""
            }`}
            style={{ left: `${(x(hover) / width) * 100}%`, top: "14%" }}
          >
            <p className="trend__tooltip-title">{labels[hover]}</p>
            {series.map((item) => (
              <p className="trend__tooltip-row" key={item.key}>
                <span
                  className="trend__tooltip-dot"
                  style={{ background: item.color }}
                />
                {item.label}:<strong>{formatValue(item.values[hover])}</strong>
              </p>
            ))}
          </div>
        )}
      </div>

      <ul className="mtrend__legend">
        {series.map((item, index) => (
          <li key={item.key}>
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <Marker
                shape={SHAPES[index % SHAPES.length]}
                cx={7}
                cy={7}
                r={4}
                fill={item.color}
              />
            </svg>
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
